"use client"

import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { getQuizSubmissionDetail } from "./api"
import { postAws } from "../postaws"
import { generatePresignedUrl } from "../postFileAws"
import { Loader, ArrowLeft, CheckCircle, XCircle, AlertCircle, FileText, Award, Target } from "lucide-react"

export default function SubmittedQuizDetails() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [quizData, setQuizData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [imageUrls, setImageUrls] = useState({})

  useEffect(() => {
    const fetchQuizDetails = async () => {
      try {
        setLoading(true)
        const response = await getQuizSubmissionDetail(id)
        const data = JSON.parse(response.body).data

        if (data.status !== "evaluated") {
          const evalResponse = await postAws("https://api-dev.aurait.ai/quizEval", "POST", { id: data._id })
          const evalData = JSON.parse(evalResponse.body)

          // Fetch the updated quiz submission details after evaluation
          const updatedResponse = await getQuizSubmissionDetail(id)
          const updatedData = JSON.parse(updatedResponse.body).data
          setQuizData(updatedData)
          await loadImages(updatedData.quizQuestions)
        } else {
          setQuizData(data)
          await loadImages(data.quizQuestions)
        }
        setError(null)
      } catch (err) {
        console.error("Error fetching submitted quiz details:", err)
        setError("Failed to load submitted quiz details. Please try again later.")
      } finally {
        setLoading(false)
      }
    }

    fetchQuizDetails()
  }, [id])

  const loadImages = async (questions) => {
    const urls = {}
    for (const question of questions) {
      if (question.questionImageUrl) {
        try {
          const url = await generatePresignedUrl(question.questionImageUrl)
          urls[question.questionImageUrl] = url
        } catch (error) {
          console.error("Error generating presigned URL:", error)
        }
      }
    }
    setImageUrls(urls)
  }

  const getQuizStats = () => {
    if (!quizData?.quizQuestions) return { total: 0, correct: 0, incorrect: 0, totalScore: 0, maxScore: 0 }

    const total = quizData.quizQuestions.length
    let correct = 0
    let totalScore = 0
    let maxScore = 0

    quizData.quizQuestions.forEach((question) => {
      if (question.answer === question.correctAnswer) {
        correct++
      }
      totalScore += question.obtainedMarks || 0
      maxScore += question.maxMarks || 0
    })

    const incorrect = total - correct

    return { total, correct, incorrect, totalScore, maxScore }
  }

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "evaluated":
        return "text-green-600 bg-green-100"
      case "submitted":
        return "text-blue-600 bg-blue-100"
      case "pending":
        return "text-yellow-600 bg-yellow-100"
      default:
        return "text-gray-600 bg-gray-100"
    }
  }

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "evaluated":
        return <CheckCircle className="w-3 h-3" />
      case "submitted":
        return <FileText className="w-3 h-3" />
      case "pending":
        return <AlertCircle className="w-3 h-3" />
      default:
        return <FileText className="w-3 h-3" />
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-lg text-blue-800 font-medium">Loading quiz results...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 text-red-600">
        <XCircle className="w-16 h-16 mb-4" />
        <p className="text-xl font-semibold">{error}</p>
        <button
          onClick={() => navigate("/quiz")}
          className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Back to Quizzes
        </button>
      </div>
    )
  }

  if (!quizData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <AlertCircle className="w-16 h-16 mb-4 text-yellow-500" />
        <p className="text-xl font-semibold">No quiz data found.</p>
        <button
          onClick={() => navigate("/quiz")}
          className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Back to Quizzes
        </button>
      </div>
    )
  }

  const quizStats = getQuizStats()

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 -mt-6">
      <div className="container mx-auto px-2 md:px-4 py-0 max-w-7xl">
        {/* Main Content Card */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-md overflow-hidden mt-6 md:mt-8">
          {/* Quiz Results Header - Single Row */}
        <div
  className="bg-gradient-to-r from-indigo-500 to-blue-600 px-4 md:px-4"
  style={{ paddingTop: "1.375rem", paddingBottom: "1.375rem" }}
>

            {/* Mobile Layout */}
            <div className="block lg:hidden">
              {/* Row 1: Back button */}
              <div className="flex items-center mb-2">
                <button
                  onClick={() => navigate("/quiz")}
                  className="flex items-center text-white hover:text-blue-100 transition-colors duration-200"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  <span className="text-xs font-medium">Back to Quizzes</span>
                </button>
              </div>

              {/* Row 2: Stats in single line */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1">
                  <div className="inline-flex items-center gap-1 border border-white/30 backdrop-blur-sm rounded-md px-1 py-0.5 text-white text-xs font-medium bg-white/10">
                    <FileText className="w-3 h-3 opacity-80" />
                    <span>
                      Total: <span className="font-bold">{quizStats.total}</span>
                    </span>
                  </div>
                  <div className="inline-flex items-center gap-1 border border-white/30 backdrop-blur-sm rounded-md px-1 py-0.5 text-white text-xs font-medium bg-white/10">
                    <CheckCircle className="w-3 h-3 opacity-100" />
                    <span>
                      Correct: <span className="font-bold">{quizStats.correct}</span>
                    </span>
                  </div>
                  <div className="inline-flex items-center gap-1 border border-white/30 backdrop-blur-sm rounded-md px-1 py-0.5 text-white text-xs font-medium bg-white/10">
                    <XCircle className="w-3 h-3 opacity-80" />
                    <span>
                      Wrong: <span className="font-bold">{quizStats.incorrect}</span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Row 3: Title */}
              <h1 className="text-base font-bold text-white mb-2">{quizData.quizTitle || "Untitled Quiz"}</h1>

              {/* Row 4: Status and Score in same line */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex items-center bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full border border-white/30">
                    {getStatusIcon(quizData.status)}
                    <span className="text-xs text-white ml-1">Status: {quizData.status || "Unknown"}</span>
                  </div>
                </div>
                <div className="flex items-center bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full border border-white/30">
                  <Award className="w-3 h-3 mr-1" />
                  <span className="text-xs font-medium text-white">
                    Score: {quizStats.totalScore}/{quizStats.maxScore}
                  </span>
                </div>
              </div>
            </div>

            {/* Desktop Layout - SINGLE ROW */}
            <div className="hidden lg:flex lg:items-center lg:justify-between">
              {/* Left section: Back button */}
              <div className="flex items-center">
                <button
                  onClick={() => navigate("/quiz")}
                  className="flex items-center text-white hover:text-blue-100 transition-colors duration-200"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  <span className="text-sm font-medium">Back to Quizzes</span>
                </button>
              </div>

              {/* Middle section: Stats */}
              <div className="flex items-center gap-2">
                <div className="inline-flex items-center gap-1 border border-white/30 backdrop-blur-sm rounded-md px-2 py-1 text-white text-xs font-medium bg-white/10">
                  <FileText className="w-3 h-3 opacity-80" />
                  <span>
                    Total: <span className="font-bold">{quizStats.total}</span>
                  </span>
                </div>
                <div className="inline-flex items-center gap-1 border border-white/30 backdrop-blur-sm rounded-md px-2 py-1 text-white text-xs font-medium bg-white/10">
                  <CheckCircle className="w-3 h-3 opacity-100" />
                  <span>
                    Correct: <span className="font-bold">{quizStats.correct}</span>
                  </span>
                </div>
                <div className="inline-flex items-center gap-1 border border-white/30 backdrop-blur-sm rounded-md px-2 py-1 text-white text-xs font-medium bg-white/10">
                  <XCircle className="w-3 h-3 opacity-80" />
                  <span>
                    Wrong: <span className="font-bold">{quizStats.incorrect}</span>
                  </span>
                </div>
              </div>

              {/* Title */}
              <div className="flex-shrink-0 mx-4">
                <h1 className="text-base font-bold text-white whitespace-nowrap">
                  {quizData.quizTitle || "Untitled Quiz"}
                </h1>
              </div>

              {/* Status and Score */}
              <div className="flex items-center gap-2">
                <div className="flex items-center bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full border border-white/30">
                  {getStatusIcon(quizData.status)}
                  <span className="text-xs text-white ml-1">Status: {quizData.status || "Unknown"}</span>
                </div>
                <div className="flex items-center bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full border border-white/30">
                  <Award className="w-3 h-3 mr-1" />
                  <span className="text-xs font-medium text-white">
                    Score: {quizStats.totalScore}/{quizStats.maxScore}
                  </span>
                </div>
              </div>

              {/* Percentage Score */}
              <div className="flex items-center">
                <div className="flex items-center bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full border border-white/30">
                  <Target className="w-3 h-3 mr-1" />
                  <span className="text-xs font-bold text-white">
                    {quizStats.maxScore > 0 ? Math.round((quizStats.totalScore / quizStats.maxScore) * 100) : 0}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-4 md:p-6">
            {/* Quiz Topic */}
            {quizData.quiz_topic && (
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h2 className="text-sm font-medium text-blue-800 mb-1">Quiz Topic</h2>
                <p className="text-blue-700">{quizData.quiz_topic}</p>
              </div>
            )}

            {/* Questions */}
            <div className="space-y-6">
              {quizData.quizQuestions &&
                quizData.quizQuestions.map((question, index) => (
                  <div key={index} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                    <div className="flex justify-between items-start mb-4">
                      <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                        <span className="bg-blue-100 text-blue-800 rounded-full w-8 h-8 flex items-center justify-center mr-3 text-sm font-bold">
                          {index + 1}
                        </span>
                        Question
                      </h2>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                          {question.maxMarks} {question.maxMarks === 1 ? "Mark" : "Marks"}
                        </span>
                        {quizData.status === "evaluated" && (
                          <span
                            className={`text-sm font-medium px-3 py-1 rounded-full ${
                              question.obtainedMarks === question.maxMarks
                                ? "text-green-600 bg-green-50"
                                : question.obtainedMarks > 0
                                  ? "text-yellow-600 bg-yellow-50"
                                  : "text-red-600 bg-red-50"
                            }`}
                          >
                            {question.obtainedMarks}/{question.maxMarks}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="text-base text-gray-700 mb-4">{question.question}</div>

                    {question.questionImageUrl && (
                      <div className="mb-4">
                        <img
                          src={imageUrls[question.questionImageUrl] || "/placeholder.svg"}
                          alt="Question Image"
                          className="max-w-full h-auto rounded-lg shadow-sm"
                          onError={(e) => {
                            e.target.onerror = null
                            e.target.src = "/placeholder.svg"
                          }}
                        />
                      </div>
                    )}

                    <div className="mt-4">
                      <h3 className="font-medium text-base mb-3 text-gray-800">Options:</h3>
                      <div className="space-y-2">
                        {question.options &&
                          question.options.map((option, optionIndex) => (
                            <div
                              key={optionIndex}
                              className={`p-3 rounded-lg border transition-all duration-200 ${
                                option === question.answer
                                  ? option === question.correctAnswer
                                    ? "bg-green-50 border-green-300 text-green-800"
                                    : "bg-red-50 border-red-300 text-red-800"
                                  : option === question.correctAnswer
                                    ? "bg-green-50 border-green-300 text-green-800"
                                    : "bg-gray-50 border-gray-200 text-gray-700"
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="flex-1">{option}</span>
                                <div className="flex items-center gap-2">
                                  {option === question.answer && (
                                    <span className="text-xs font-semibold px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                                      Your Answer
                                    </span>
                                  )}
                                  {option === question.correctAnswer && (
                                    <div className="flex items-center">
                                      <CheckCircle className="w-4 h-4 text-green-600" />
                                      <span className="text-xs font-semibold ml-1 text-green-600">Correct</span>
                                    </div>
                                  )}
                                  {option === question.answer && option !== question.correctAnswer && (
                                    <XCircle className="w-4 h-4 text-red-600" />
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Footer Summary */}
          <div className="bg-gradient-to-r from-gray-50 to-blue-50 px-4 md:px-6 py-4 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Quiz Status:</span>
                  <span
                    className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(quizData.status)}`}
                  >
                    {quizData.status || "Unknown"}
                  </span>
                </div>
              </div>

              {quizData.status === "evaluated" && (
                <div className="flex items-center gap-4">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Accuracy:</span>
                    <span className="ml-2 font-bold text-blue-600">
                      {quizStats.total > 0 ? Math.round((quizStats.correct / quizStats.total) * 100) : 0}%
                    </span>
                  </div>
                  <div className="text-lg font-bold text-green-600 bg-green-100 px-4 py-2 rounded-full">
                    Final Score: {quizStats.totalScore}/{quizStats.maxScore}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
